import fs from "fs";
import pdfParse from "pdf-parse";
import mammoth from "mammoth";
import { Document } from "docx";
import xlsx from "xlsx";
import sentenceSplitter from "sentence-splitter";
import { logger } from "../../../shared/logger";

/**
 * Function: smartChunkText
 * Description: Split text into chunks by sentences, each chunk has a max length. Fallback to splitting by period if sentence splitter fails.
 * Params: text (string) - The input text; chunkSize (number) - Max length per chunk (default 2000).
 * Returns: string[] - Array of text chunks.
 */
export function smartChunkText(text: string, chunkSize = 2000): string[] {
  if (!text || typeof text !== "string") return [];
  let sentences: any[] = [];
  try {
    // Try to split text into sentences using sentenceSplitter
    const splitResult = sentenceSplitter.split(text);
    if (!Array.isArray(splitResult)) return [];
    sentences = splitResult;
  } catch (err) {
    // Fallback: If sentenceSplitter fails, split by period (.)
    sentences = text.split(".").map((s: string) => s + ".");
  }
  if (!Array.isArray(sentences) || sentences.length === 0) return [];
  // If fallback, sentences is an array of strings; otherwise, it's an array of objects
  const rawSentences =
    typeof sentences[0] === "string"
      ? sentences
      : sentences.map((s: any) => s.raw);
  const chunks: string[] = [];
  let current = "";
  for (const sentence of rawSentences) {
    if ((current + sentence).length > chunkSize) {
      if (current) chunks.push(current);
      current = "";
    }
    current += sentence;
  }
  if (current) chunks.push(current);
  return chunks;
}

/**
 * Function: readAndChunk
 * Description: Read file content and split into smart chunks based on file type.
 * Params: filePath (string) - Path to file; fileName (string) - Name of file.
 * Returns: Promise<string[]> - Array of text chunks.
 */
export async function readAndChunk(
  filePath: string,
  fileName: string
): Promise<string[]> {
  if (!fileName || typeof fileName !== "string") {
    logger.error(
      "readAndChunk: fileName is invalid",
      new Error(String(fileName))
    );
    throw Object.assign(new Error("Invalid fileName: " + fileName), {
      status: 400,
    });
  }
  if (!filePath || typeof filePath !== "string") {
    logger.error(
      "readAndChunk: filePath is invalid",
      new Error(String(filePath))
    );
    throw Object.assign(new Error("Invalid filePath: " + filePath), {
      status: 400,
    });
  }

  // Get file extension
  const parts = fileName.split(".");
  if (!parts || parts.length < 2) {
    logger.error("File name does not have an extension: " + fileName);
    throw Object.assign(
      new Error("File name does not have an extension: " + fileName),
      { status: 400 }
    );
  }
  const ext = parts.pop()!.toLowerCase();

  let text = "";

  if (ext === "pdf") {
    const dataBuffer = fs.readFileSync(filePath);
    const data = await pdfParse(dataBuffer);
    text = data.text;
  } else if (ext === "txt") {
    text = fs.readFileSync(filePath, "utf-8");
  } else if (ext === "docx") {
    const data = fs.readFileSync(filePath);
    const result = await mammoth.extractRawText({ buffer: data });
    text = result.value;
  } else if (ext === "xlsx") {
    const workbook = xlsx.readFile(filePath);
    text = workbook.SheetNames.map((name: string) =>
      xlsx.utils.sheet_to_csv(workbook.Sheets[name])
    ).join("\n");
  } else {
    throw new Error("Unsupported file type: " + ext);
  }

  if (!text || typeof text !== "string" || !text.trim()) {
    logger.error(`No text extracted from file: ${fileName} ext: ${ext}`);
    throw Object.assign(
      new Error(
        `Không thể trích xuất nội dung từ file: ${fileName} (loại: ${ext}) - Có thể file PDF là scan hoặc không có text layer, hoặc file bị lỗi.`
      ),
      { status: 400 }
    );
  }

  return smartChunkText(text);
}

/**
 * Function: chunkText
 * Description: Split text into chunks by character count.
 * Params: text (string) - The input text; chunkSize (number) - Max length per chunk (default 1500).
 * Returns: string[] - Array of text chunks.
 */
export function chunkText(text: string, chunkSize = 1500): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < text.length; i += chunkSize) {
    chunks.push(text.slice(i, i + chunkSize));
  }
  return chunks;
}

// TypeScript: Add declaration for pdf-parse if missing
declare module "pdf-parse";
